import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Button } from "@stackje/shared-components/ui/button";
import { Input } from "@stackje/shared-components/ui/input";
import { Label } from "@stackje/shared-components/ui/label";
import { useSignup } from "@/hooks/useSignup";
import type { AdminRouterContext } from "@/context/AdminRouterContext";
import { OtpVerification } from "@/components/shared/OtpVerification";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Mail } from "lucide-react";

export const Route = createFileRoute("/auth/signup")({
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: (search.returnTo as string) || undefined,
  }),
  beforeLoad: async ({
    context,
    search,
  }: {
    context: AdminRouterContext;
    search: { returnTo?: string };
  }) => {
    if (context.auth.session) {
      if (search.returnTo) {
        throw redirect({ href: search.returnTo, replace: true });
      }
      throw redirect({
        to: "/",
        replace: true,
      });
    }
    return {};
  },
  component: SignupComponent,
});

function SignupComponent() {
  const { returnTo } = Route.useSearch();
  const isOAuthFlow = returnTo?.includes("/oauth/");
  const {
    email,
    setEmail,
    otpCode,
    setOtpCode,
    formErrors,
    apiError,
    message,
    loading,
    showForm,
    showOtpInput,
    isLoggedIn,
    isFormValid,
    handleSignup,
    handleOtpVerification,
  } = useSignup(returnTo);

  return (
    <AuthLayout oauthContext={isOAuthFlow}>
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 mb-1">
          {showOtpInput
            ? "Check your email"
            : isLoggedIn
              ? "You're in!"
              : "Create your site"}
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          {showOtpInput
            ? "Enter the code we sent you"
            : isLoggedIn
              ? "Your account is ready."
              : "Get started for free with just your email. No password, no credit card."}
        </p>

        {message && (
          <div className="rounded-lg bg-green-50 border border-green-100 px-4 py-3 mb-6">
            <p className="text-green-700 text-sm">{message}</p>
            {!showForm && !showOtpInput && isLoggedIn && (
              <Link
                to="/"
                className="inline-block mt-2 text-sm font-medium text-green-700 hover:text-green-800 underline underline-offset-2"
              >
                Claim your site name &rarr;
              </Link>
            )}
          </div>
        )}

        {apiError && (
          <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 mb-6">
            <p className="text-red-700 text-sm">{apiError}</p>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSignup} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-gray-700">
                Email address
              </Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-10"
                  aria-invalid={formErrors?.email ? "true" : "false"}
                />
              </div>
              {formErrors?.email && (
                <p className="text-red-500 text-xs mt-1.5">
                  {formErrors.email._errors.join(", ")}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !isFormValid}
            >
              {loading ? "Creating account..." : "Continue with email"}
            </Button>
          </form>
        )}

        {showOtpInput && (
          <OtpVerification
            email={email}
            otpCode={otpCode}
            setOtpCode={setOtpCode}
            loading={loading}
            onVerify={handleOtpVerification}
            description="Enter the 6-digit code from your email, or click the link in the email to verify automatically."
          />
        )}

        {showForm && (
          <>
            <p className="mt-8 text-center text-sm text-gray-500">
              Already have an account?{" "}
              <Link
                to="/auth/login"
                search={{ returnTo: returnTo }}
                className="font-medium text-orange-600 hover:text-orange-700"
              >
                Sign in
              </Link>
            </p>

            <p className="mt-3 text-center text-xs text-gray-400">
              We&apos;ll send a verification code to your email.
              <br />
              Same process whether you&apos;re signing in or signing up.
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
